import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChatBubbleLeftRightIcon, InboxIcon, MegaphoneIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import { useFamilyThreads } from '../../hooks/useFamilyThreads';
import { useSyncParentContext } from '../../hooks/useSyncParentContext';
import { useListMyMessagesQuery, useMarkAllReadMutation, useMarkMessageReadMutation } from '../../store/api/localApi';
import { MessageDeliveryStatus, MessageType, ThreadStatus } from '../../types';
import MessageDetailModal from '../../components/messages/MessageDetailModal';
import ChildPickerSheet from '../../components/parent/ChildPickerSheet';
import NotificationNudge from '../../components/NotificationNudge';
import { dublinDateString, formatDublinDate, formatDublinTime } from '../../utils/date';
import { buildFamilyInbox, replyStateLine } from './family-inbox';

const when = (iso: string) => (dublinDateString(new Date(iso)) === dublinDateString() ? formatDublinTime(iso) : formatDublinDate(iso));

/**
 * /parent/family/inbox — the family's messages. A family page: it never
 * reads or changes the active child; every row names its child instead.
 */
const FamilyInbox: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { children, homeChildId, isLoading: childrenLoading } = useActiveStudent();
  const childIds = useMemo(() => children.map((c) => c.id), [children]);
  const { threadsByChild, isLoading: threadsLoading } = useFamilyThreads(childIds);
  const { data, isLoading: messagesLoading, error } = useListMyMessagesQuery({ limit: 50 });
  const [markAllRead, { isLoading: isMarkingAll }] = useMarkAllReadMutation();
  const [markMessageRead] = useMarkMessageReadMutation();
  const syncParentContext = useSyncParentContext();

  const deliveries = useMemo(() => data?.deliveries ?? [], [data]);
  const { conversations, sends } = useMemo(
    () => buildFamilyInbox({ children, deliveries, threadsByChild }),
    [children, deliveries, threadsByChild]
  );
  const anyUnread = deliveries.some((d) => d.status === MessageDeliveryStatus.UNREAD);

  // The id, not the row: the row is re-derived from the live `sends` so a reply
  // sent from the popup is reflected the moment the inbox re-fetches.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = sends.find((s) => s.delivery.id === selectedId) ?? null;
  const [pickOpen, setPickOpen] = useState(false);
  const isLoading = childrenLoading || messagesLoading || threadsLoading;

  // A family route: reading Aoife's conversation must not make Aoife active.
  // `replace` is for the ?open= path: the push-tap entry is swapped out, so Back
  // from the conversation returns to the plain inbox instead of re-opening it.
  const openConversation = useCallback(
    (threadId: string, { replace = false }: { replace?: boolean } = {}) => {
      deliveries.filter((d) => d.threadId === threadId && d.status === MessageDeliveryStatus.UNREAD).forEach((d) => markMessageRead(d.id));
      navigate(`/parent/family/threads/${threadId}`, { replace });
    },
    [deliveries, markMessageRead, navigate]
  );

  // ?open=<deliveryId> from a push tap: a thread post opens its conversation,
  // anything else opens the popup. Handled once, and the param is stripped
  // either way — the thread hop replaces the ?open= entry, the other branches
  // rewrite it — so neither Back nor a stale id (withdrawn delivery)
  // re-triggers it.
  const handledOpen = useRef(false);
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || handledOpen.current || isLoading) return;
    handledOpen.current = true;
    const match = deliveries.find((d) => d.id === openId);
    const isThreadPost = match && match.type === MessageType.PARENT_DM && match.threadId && match.repliesAllowed === undefined;
    if (isThreadPost) { openConversation(match.threadId!, { replace: true }); return; }
    if (sends.some((s) => s.delivery.id === openId)) setSelectedId(openId);
    const next = new URLSearchParams(searchParams); next.delete('open');
    navigate({ search: next.toString() }, { replace: true });
  }, [searchParams, isLoading, deliveries, sends, openConversation, navigate]);

  const liveChildren = children.filter((c) => c.status !== 'LEFT');
  // Switch the PARENT context to the child's location before the thread page
  // loads, as ChildSwitcher does, so its first requests carry the right
  // X-Active-Context.
  const openChildThreads = async (child: { id: string; locationId: string; organizationId: string }) => {
    await syncParentContext(child.locationId, child.organizationId);
    navigate(`/parent/${child.id}/threads`);
  };
  const messageStaff = () => {
    if (liveChildren.length === 1) { void openChildThreads(liveChildren[0]); return; }
    setPickOpen(true);
  };
  const preselected = homeChildId ?? liveChildren[0]?.id;

  if (isLoading) return <div className="py-12 text-center text-sm text-gray-500">Loading messages…</div>;
  if (error) return <div className="py-12 text-center"><InboxIcon className="mx-auto h-12 w-12 text-red-400" /><p className="mt-2 text-sm text-gray-500">Error loading messages. Please try again later.</p></div>;

  return (
    <div className="py-6" data-testid="family-inbox">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
        <NotificationNudge />
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-gray-900">Inbox</h1>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => markAllRead()} disabled={!anyUnread || isMarkingAll} data-testid="messages-mark-all-read" className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed">
              {isMarkingAll ? 'Marking…' : 'Mark all read'}
            </button>
            {liveChildren.length > 0 && (
              <button type="button" onClick={messageStaff} data-testid="inbox-message-staff" className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700">
                <PencilSquareIcon className="h-4 w-4" /> Message staff
              </button>
            )}
          </div>
        </div>

        {conversations.length === 0 && sends.length === 0 && (
          <div data-testid="inbox-empty" className="text-center py-16"><InboxIcon className="mx-auto h-12 w-12 text-gray-300" /><p className="mt-3 text-sm text-gray-500">Your inbox is empty.</p></div>
        )}

        {conversations.length > 0 && (
          <section data-testid="inbox-conversations" className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500">Conversations</div>
            <ul className="divide-y divide-gray-100">
              {conversations.map((row) => (
                <li key={row.threadId}>
                  <button type="button" onClick={() => openConversation(row.threadId)} data-testid={`inbox-conversation-${row.threadId}`} className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 ${row.unread ? 'bg-blue-50' : ''}`}>
                    <div className="mt-1.5 w-2 flex-shrink-0">{row.unread && <span data-testid={`inbox-conversation-${row.threadId}-unread-dot`} aria-label="Unread" className="block h-2 w-2 rounded-full bg-blue-500" />}</div>
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm truncate ${row.unread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{row.childName} · {row.subject}</span>
                        <span className="flex-shrink-0 text-xs text-gray-400">{when(row.lastMessageAt)}</span>
                      </div>
                      <p className="text-sm text-gray-500 truncate mt-0.5">{row.lastLine}</p>
                      <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${row.status === ThreadStatus.OPEN ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
                        {row.status === ThreadStatus.OPEN ? 'Waiting for a reply' : 'Answered'}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {sends.length > 0 && (
          <section data-testid="inbox-from-service" className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500">From the service</div>
            <ul className="divide-y divide-gray-100">
              {sends.map((send) => {
                const d = send.delivery;
                const unread = d.status === MessageDeliveryStatus.UNREAD;
                const state = replyStateLine(send);
                return (
                  <li key={d.id}>
                    <button type="button" onClick={() => setSelectedId(d.id)} data-testid={`inbox-send-${d.id}`} className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 ${unread ? 'bg-blue-50' : ''}`}>
                      <div className="mt-1.5 w-2 flex-shrink-0">{unread && <span data-testid={`inbox-send-${d.id}-unread-dot`} aria-label="Unread" className="block h-2 w-2 rounded-full bg-blue-500" />}</div>
                      {d.type === MessageType.ANNOUNCEMENT ? <MegaphoneIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" /> : <span className="h-5 w-5 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm truncate ${unread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{d.studentName ? `${d.studentName.split(' ')[0]} · ` : ''}{d.title}</span>
                          <span className="flex-shrink-0 text-xs text-gray-400">{when(d.createdAt)}</span>
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{d.senderName}</p>
                        <p className="text-sm text-gray-500 truncate mt-0.5">{d.bodyPreview}</p>
                        {state && <p data-testid={`inbox-send-${d.id}-reply-state`} className="text-xs font-medium text-primary-600 mt-0.5">{state}</p>}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {data?.nextCursor && <p className="text-center text-xs text-gray-400">Showing the first 50 messages.</p>}
      </div>

      {selected && (
        <MessageDetailModal delivery={selected.delivery} open onClose={() => setSelectedId(null)} repliedThreads={selected.repliedFor} />
      )}
      <ChildPickerSheet
        open={pickOpen}
        onClose={() => setPickOpen(false)}
        title="Message staff about"
        options={liveChildren}
        selectedId={preselected}
        showLocation={new Set(children.map((c) => c.locationId)).size > 1}
        onPick={(c) => { setPickOpen(false); void openChildThreads(children.find((k) => k.id === c.id)!); }}
        testIdPrefix="message-staff-child"
      />
    </div>
  );
};
export default FamilyInbox;
